import { noop } from '@chillibean/component-library';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { useAppContext } from '../../../../../context/AppContext';
import WarningAlert from '../../../../../WarningAlert/WarningAlert';
import staticText from '../../staticText.json';
import { handleFormTemplateFailure, handleFormTemplateSuccess } from '../../utils/formTemplateNotifications';
import { validateFormTemplate, validateAField } from '../../utils/formTemplateValidation';
import AddAField from '../AddAField/AddAField';
import DisablingFieldWarning from '../presentational/DisablingFieldWarning/DisablingFieldWarning';
import FormTemplateFooter from '../presentational/formTemplate/FormTemplateFooter/FormTemplateFooter';
import FormTemplateHeader from '../presentational/formTemplate/FormTemplateHeader/FormTemplateHeader';
import UpdateConfirmation from '../presentational/UpdateConfirmation/UpdateConfirmation';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

function FormTemplateFormContainer(props) {
  const {
    formTemplate,
    handleTemplateChange = noop,
    addAField = noop,
    handleFormAction = noop,
    formId,
    setActiveAccordion = noop,
    readOnly = false,
    setFormTemplatesError = noop,
  } = props;

  const { notificationDomRef } = useAppContext();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [validateAllFields, setValidateAllFields] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [changeReasonValue, setChangeReasonValue] = useState('');
  const [validateTemplateHeader, setValidateTemplateHeader] = useState({
    title: { errorMessage: '', isError: false },
    description: { errorMessage: '', isError: false },
  });
  const [initialTemplate, setInitialTemplate] = useState(false);
  const [showDisablingPersistedFieldWarning, setShowDisablingPersistedFieldWarning] = useState(false);
  const [isLinkClicked, setIsLinkClicked] = useState(false);

  const prevFormTemplate = usePrevious(formTemplate);
  const isFormTemplateChanged = !_.isEqual(prevFormTemplate, initialTemplate);

  const isFieldBeingDisabled = (fieldValues) => {
    const isBeingDisabled = fieldValues?.enabled;

    if (isBeingDisabled) setShowDisablingPersistedFieldWarning(true);
  };

  const closeDisablingPersistedFieldWarning = () => setShowDisablingPersistedFieldWarning(false);

  const handleChangeReason = (event) => {
    setChangeReasonValue(event.target.value);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleSaveSuccess = () => {
    handleFormTemplateSuccess(notificationDomRef, setFormTemplatesError, 'saveTemplate');
    setTimeout(() => {
      history.push('/');
    }, 2500);
  };

  const handleApiSave = async () => {
    try {
      setIsLoading(true);
      if (formId) {
        await handleFormAction(formId, changeReasonValue);
      } else {
        await handleFormAction();
      }
      handleSaveSuccess();
    } catch (error) {
      handleFormTemplateFailure(error, notificationDomRef, setFormTemplatesError, 'saveTemplate');
      setIsLoading(false);
      setShowModal(false);
    }
  };

  const handleSave = () => {
    if (isLoading) return;
    setValidateAllFields(true);

    const validateTemplate = validateFormTemplate(formTemplate, true);
    if (!validateTemplate || validateTemplate.length > 0) {
      setActiveAccordion(validateTemplate.length ? validateTemplate : [-1]);
      return;
    }

    if (formId) {
      setShowModal(true);
    } else {
      handleApiSave();
    }
  };

  const isUpdateButtonDisable = () => {
    return _.isEqual(formTemplate, initialTemplate) || isLoading;
  };

  const saveButtonProps = {
    handleFormAction: handleSave,
    isDisable: formId ? isUpdateButtonDisable() : isLoading,
    showLoadingIcon: isLoading,
    buttonTitle: `${formId ? staticText.buttons.update_form : staticText.buttons.save_form}`,
  };

  const handleTemplateHeaderValidation = (fieldValue) => {
    const templateHeaderValidation = {
      title: validateAField(
        [
          { fieldType: 'blankField', fieldValue: formTemplate.title },
          { fieldType: 'maximumCharacters', fieldValue: formTemplate.title },
        ],
        { maxChars: 140 },
      ),
      description: validateAField([{ fieldType: 'maximumCharacters', fieldValue: formTemplate.description }], {
        maxChars: 500,
      }),
    };
    return templateHeaderValidation[fieldValue];
  };

  const handleTemplateHeaderOnBlur = (fieldValue) => {
    const dupValidateTemplateHeader = { ...validateTemplateHeader };
    dupValidateTemplateHeader[fieldValue] = handleTemplateHeaderValidation(fieldValue);
    setValidateTemplateHeader(dupValidateTemplateHeader);
  };

  const handleTemplateHeaderOnClick = (fieldValue) => {
    const dupValidateTemplateHeader = { ...validateTemplateHeader };
    dupValidateTemplateHeader[fieldValue] = { errorMessage: '', isError: false };
    setValidateTemplateHeader(dupValidateTemplateHeader);
  };

  useEffect(() => {
    if (validateAllFields) {
      setValidateTemplateHeader({
        title: handleTemplateHeaderValidation('title'),
        description: handleTemplateHeaderValidation('description'),
      });
    }
  }, [validateAllFields]);

  useEffect(() => {
    if (
      !initialTemplate ||
      initialTemplate?.id !== formTemplate?.id ||
      initialTemplate?.version_number !== formTemplate?.version_number
    ) {
      setInitialTemplate(_.cloneDeep(formTemplate));
    }
  }, [formTemplate]);

  return (
    <>
      <div className="template-builder_content-area">
        <FormTemplateHeader
          formTemplate={formTemplate}
          handleTemplateChange={handleTemplateChange}
          handleTemplateHeaderOnBlur={handleTemplateHeaderOnBlur}
          handleTemplateHeaderOnClick={handleTemplateHeaderOnClick}
          validateTemplateHeader={validateTemplateHeader}
          readOnly={readOnly}
        />

        <AddAField
          formTemplate={formTemplate}
          addAField={addAField}
          validateAllFields={validateAllFields}
          isFieldBeingDisabled={isFieldBeingDisabled}
        />
      </div>

      <FormTemplateFooter saveButton={saveButtonProps} readOnly={readOnly} formTemplateID={formId} />

      {Boolean(formId) && (
        <UpdateConfirmation
          showModal={showModal}
          handleChangeReason={handleChangeReason}
          changeReasonValue={changeReasonValue}
          closeModal={closeModal}
          handleSave={handleApiSave}
          showLoadingIcon={isLoading}
        />
      )}

      {showDisablingPersistedFieldWarning && (
        <DisablingFieldWarning
          showModal={showDisablingPersistedFieldWarning}
          closeModal={closeDisablingPersistedFieldWarning}
        />
      )}

      {!isLinkClicked && isFormTemplateChanged && (
        <WarningAlert
          isLinkClicked={isLinkClicked}
          setIsLinkClicked={setIsLinkClicked}
          displayModal={false}
          title="You have unsaved changes"
          alertMessage="You are about to discard all the data you have entered. This action is irreversible."
        />
      )}
    </>
  );
}

FormTemplateFormContainer.propTypes = {
  addAField: PropTypes.func,
  formTemplate: PropTypes.shape().isRequired,
  formId: PropTypes.number.isRequired,
  handleFormAction: PropTypes.func,
  handleTemplateChange: PropTypes.func,
  readOnly: PropTypes.bool,
  setActiveAccordion: PropTypes.func,
  setFormTemplatesError: PropTypes.func,
};

export default FormTemplateFormContainer;
